import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ContactHero from './components/ContactHero';
import PartnerForm from './components/PartnerForm';
import MentorForm from './components/MentorForm';
import StudentForm from './components/StudentForm';
import ContactInfoSidebar from './components/ContactInfoSidebar';
import LegalSection from './components/LegalSection';

type TabType = 'partner' | 'mentor' | 'student';

const tabs: { id: TabType; label: string; icon: string; badge: string; desc: string }[] = [
  {
    id: 'partner',
    label: '平台合作諮詢',
    icon: 'ri-building-4-line',
    badge: '企業 / 機構',
    desc: '人才培訓、課程聯開、品牌合作',
  },
  {
    id: 'mentor',
    label: '導師入駐申請',
    icon: 'ri-user-star-line',
    badge: '業界專家',
    desc: '分享經驗、輔導學員、共創課程',
  },
  {
    id: 'student',
    label: '學員諮詢',
    icon: 'ri-customer-service-2-line',
    badge: '在讀學員',
    desc: '購買問題、退款申請、帳號協助',
  },
];

const formMeta: Record<TabType, { title: string; desc: string }> = {
  partner: {
    title: '填寫合作需求',
    desc: '提交後，我們的商務團隊將在 1–2 個工作日內與你聯繫。',
  },
  mentor: {
    title: '申請成為導師',
    desc: '我們收到申請後會在 3–5 個工作日內完成審核，請留意電子郵件通知。',
  },
  student: {
    title: '提交你的問題',
    desc: '填寫訂單號和問題描述，我們會在 1 個工作日內回覆到你的信箱。',
  },
};

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as TabType) || 'partner';
  const [activeTab, setActiveTab] = useState<TabType>(
    ['partner', 'mentor', 'student'].includes(initialTab) ? initialTab : 'partner'
  );

  return (
    <div className="min-h-screen bg-[#F8F9FC] font-sans">
      <Navbar />

      <div style={{ paddingTop: '64px' }}>
        <ContactHero />

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
            {/* Form Area */}
            <div className="flex-1 min-w-0">
              {/* Tabs */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-7">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 p-4 rounded-2xl border-2 text-left cursor-pointer transition-all ${
                      activeTab === tab.id
                        ? 'border-violet-500 bg-white'
                        : 'border-transparent bg-white/60 hover:bg-white hover:border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl transition-colors ${
                        activeTab === tab.id ? 'bg-violet-600' : 'bg-slate-100'
                      }`}>
                        <i className={`${tab.icon} text-base ${activeTab === tab.id ? 'text-white' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-bold transition-colors ${activeTab === tab.id ? 'text-violet-700' : 'text-[#0A1628]'}`}>
                          {tab.label}
                        </p>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                          activeTab === tab.id ? 'bg-violet-100 text-violet-600' : 'bg-slate-100 text-slate-400'
                        }`}>
                          {tab.badge}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 ml-12">{tab.desc}</p>
                  </button>
                ))}
              </div>

              {/* Form Card */}
              <div className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-8">
                <div className="mb-7">
                  <h2 className="text-lg font-bold text-[#0A1628] mb-1">
                    {formMeta[activeTab].title}
                  </h2>
                  <p className="text-sm text-slate-400">
                    {formMeta[activeTab].desc}
                  </p>
                </div>

                {activeTab === 'partner' && <PartnerForm />}
                {activeTab === 'mentor' && <MentorForm />}
                {activeTab === 'student' && <StudentForm />}
              </div>
            </div>

            {/* Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <ContactInfoSidebar activeTab={activeTab} />
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Strip */}
        <div className="bg-white border-t border-slate-100 py-10 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-2 mb-6 sm:mb-7">
              <div className="w-1 h-5 bg-violet-600 rounded-full" />
              <h3 className="text-base font-bold text-[#0A1628]">常見問題</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[
                {
                  q: '導師入駐有什麼基本要求？',
                  a: '我們要求申請導師目前在外商或知名科技/顧問公司擔任中高階職位，有至少 3 年以上的實際工作經驗，並具備輔導他人的意願與時間。',
                },
                {
                  q: '合作可以做哪些形式？',
                  a: '包括但不限於：為公司員工客製內訓課程、共同開發付費課程、品牌冠名內容專欄、校園招募配合、課程嵌入企業 HR 流程等。',
                },
                {
                  q: '服務費是如何計算的？',
                  a: '服務費根據服務內容的不同而有所差異，沒有統一固定收費。歡迎透過電子郵件或 LINE 聯絡我們，團隊會根據你的具體需求提供詳細說明與報價。',
                },
                {
                  q: '提交申請後多久會有回音？',
                  a: '合作諮詢通常在 1–2 個工作日內由對口商務人員聯繫；導師入駐審核約需 3–5 個工作日，結果將傳送至申請時填寫的信箱。',
                },
                {
                  q: '付款後沒收到郵件怎麼辦？',
                  a: '請先檢查垃圾郵件夾。如仍未收到，點擊上方「學員諮詢」Tab，填寫姓名、信箱和訂單號，我們會在 1 個工作日內處理。',
                },
                {
                  q: '導師可以隨時暫停接單嗎？',
                  a: '可以。導師可以在個人後台隨時調整接單狀態、暫停行事曆或修改可約時間段，完全彈性，沒有任何鎖定期。',
                },
              ].map((faq) => (
                <div key={faq.q} className="bg-[#F8F9FC] rounded-xl p-5 border border-slate-100">
                  <div className="flex items-start gap-2.5 mb-2.5">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-question-line text-violet-500 text-sm" />
                    </div>
                    <p className="text-sm font-semibold text-[#0A1628] leading-snug">{faq.q}</p>
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed pl-7">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <LegalSection />
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
